import { MessageSquare, MoreVertical, RotateCcw, Search } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDateTime } from "@/lib/constants";
import { matchesSearch } from "@/lib/search-utils";
import {
  type BreakdownQuestion,
  type BreakdownResponse,
  type BreakdownRow,
  buildSurveyBreakdown,
  questionKind,
  responseJobTitle,
  responseTeam,
  sortQuestions,
} from "@/lib/survey-breakdown";

type ResultsResponse = BreakdownResponse & {
  id: string;
  submittedAt?: string | Date | null;
  employee?: { fullName?: string | null; jobTitle?: string | null } | null;
};

type Props = {
  responses: ResultsResponse[];
  questionInstances: BreakdownQuestion[];
  /**
   * The server's call: whether this payload carries answer content. Without
   * it the grouped tab shows participation only, and the per-person tab shows
   * answers only on the rows the server still sent them for (a leader's own
   * reports).
   */
  canViewResponses: boolean;
  /** Shows the per-row "Resetar resposta" action. */
  canOperateSurveys: boolean;
  onResetResponse: (response: { id: string; name: string }) => void;
  resetPending?: boolean;
};

const KIND_LABEL = { scale: "nota", text: "texto", choice: "escolha" } as const;

const isAnswered = (status: string) => status === "completed" || status === "finished";

const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const formatAverage = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : value.toFixed(1).replace(".", ",");

const formatChoices = (counts: Record<string, number> | undefined) => {
  const entries = Object.entries(counts ?? {}).sort((a, b) => b[1] - a[1]);
  return entries.length ? entries.map(([option, n]) => `${option} ${n}`).join(" · ") : "—";
};

/**
 * The responses of one survey instance in two tabs: one row per person (with
 * cargo, time and each answer inline) and the results grouped by time or by
 * cargo. Questions are listed once, above the tables, as P1, P2…
 */
export function SurveyResultsCard({
  responses,
  questionInstances,
  canViewResponses,
  canOperateSurveys,
  onResetResponse,
  resetPending,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [groupBy, setGroupBy] = useState<"team" | "jobTitle">("team");
  const [openComments, setOpenComments] = useState<Set<string>>(new Set());

  const finished = useMemo(() => responses.filter((r) => isAnswered(r.status)), [responses]);
  // A leader who is also an operator still gets answers for their own reports.
  const showAnswerColumns = canViewResponses || finished.some((r) => Array.isArray(r.answers));
  const questions = useMemo(() => sortQuestions(questionInstances), [questionInstances]);
  const breakdown = useMemo(
    () => buildSurveyBreakdown(responses, questionInstances, { withAnswers: canViewResponses }),
    [responses, questionInstances, canViewResponses],
  );

  const filtered = finished.filter(
    (r) => !searchTerm || matchesSearch(r.employee?.fullName || "", searchTerm),
  );

  const toggleComments = (key: string) =>
    setOpenComments((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const answerCell = (response: ResultsResponse, question: BreakdownQuestion) => {
    if (!Array.isArray(response.answers)) return <span className="text-muted-foreground">—</span>;
    const answer = response.answers.find((a) => a.pulseQuestionInstanceId === question.id);
    const kind = questionKind(question.questionType);
    const value = kind === "scale" ? answer?.answerValue : answer?.answerText?.trim();
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    return kind === "scale" ? (
      <span className="tabular-nums">{value}</span>
    ) : (
      <span className="whitespace-pre-wrap">{value}</span>
    );
  };

  const groupedTable = (rows: BreakdownRow[], label: string) => (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{label}</TableHead>
            <TableHead className="text-right">Convidados</TableHead>
            <TableHead className="text-right">Responderam</TableHead>
            <TableHead className="text-right">% resposta</TableHead>
            {breakdown.questions.map((q, i) => (
              <TableHead
                key={q.id}
                className={questionKind(q.questionType) === "scale" ? "text-right" : undefined}
                title={q.questionText}
              >
                P{i + 1}
                {questionKind(q.questionType) === "scale" ? " · média" : ""}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const openKeys = breakdown.questions
              .map((q) => `${groupBy}:${row.group}:${q.id}`)
              .filter((k) => openComments.has(k));
            return (
              <Fragment key={row.group}>
                <TableRow data-testid={`breakdown-row-${row.group}`}>
                  <TableCell className="font-medium whitespace-nowrap">{row.group}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.invited}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.answered}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.responseRate}%</TableCell>
                  {breakdown.questions.map((q) => {
                    const kind = questionKind(q.questionType);
                    if (kind === "scale") {
                      return (
                        <TableCell key={q.id} className="text-right tabular-nums">
                          {formatAverage(row.averages[q.id])}
                        </TableCell>
                      );
                    }
                    if (kind === "choice") {
                      return (
                        <TableCell key={q.id} className="whitespace-nowrap">
                          {formatChoices(row.choices[q.id])}
                        </TableCell>
                      );
                    }
                    const n = row.comments[q.id]?.length ?? 0;
                    const key = `${groupBy}:${row.group}:${q.id}`;
                    return (
                      <TableCell key={q.id}>
                        {n === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <button
                            type="button"
                            className="text-sm text-primary hover:underline"
                            aria-expanded={openComments.has(key)}
                            onClick={() => toggleComments(key)}
                            data-testid={`button-comments-${row.group}-${q.id}`}
                          >
                            {n} {n === 1 ? "comentário" : "comentários"}
                          </button>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {openKeys.map((key) => {
                  const qId = key.split(":").pop() as string;
                  const index = breakdown.questions.findIndex((q) => q.id === qId);
                  return (
                    <TableRow
                      key={key}
                      className="bg-[hsl(var(--paper-2))] hover:bg-[hsl(var(--paper-2))]"
                    >
                      <TableCell colSpan={4 + breakdown.questions.length} className="pl-8">
                        <p className="text-xs text-muted-foreground mb-2">
                          P{index + 1} · comentários de {row.group}
                        </p>
                        <ul className="space-y-2 max-w-3xl">
                          {row.comments[qId].map((c, i) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: comments are static and never reordered
                            <li key={i} className="rounded-md border bg-card px-3 py-2 text-sm">
                              <span className="whitespace-pre-wrap">{c.text}</span>
                              {groupBy === "team" && (
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                  {c.jobTitle}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell>Total</TableCell>
            <TableCell className="text-right tabular-nums">{breakdown.total.invited}</TableCell>
            <TableCell className="text-right tabular-nums">{breakdown.total.answered}</TableCell>
            <TableCell className="text-right tabular-nums">
              {breakdown.total.responseRate}%
            </TableCell>
            {breakdown.questions.map((q) => {
              const kind = questionKind(q.questionType);
              const t = breakdown.total;
              return (
                <TableCell
                  key={q.id}
                  className={kind === "scale" ? "text-right tabular-nums" : "whitespace-nowrap"}
                >
                  {kind === "scale"
                    ? formatAverage(t.averages[q.id])
                    : kind === "choice"
                      ? formatChoices(t.choices[q.id])
                      : `${t.comments[q.id]?.length ?? 0} comentários`}
                </TableCell>
              );
            })}
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );

  return (
    <Card data-testid="card-survey-results">
      <CardHeader>
        <CardTitle className="text-lg">
          {canViewResponses ? "Respostas dos Colaboradores" : "Já responderam"}
        </CardTitle>
        <CardDescription>
          {finished.length === 0
            ? "Nenhuma resposta recebida ainda"
            : `${finished.length} de ${responses.length} colaboradores responderam`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {finished.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aguardando respostas dos colaboradores</p>
          </div>
        ) : (
          <Tabs defaultValue="person">
            <TabsList>
              <TabsTrigger value="person" data-testid="tab-results-person">
                Por pessoa
              </TabsTrigger>
              <TabsTrigger value="group" data-testid="tab-results-group">
                Por time e cargo
              </TabsTrigger>
            </TabsList>

            {showAnswerColumns && questions.length > 0 && (
              <ol className="mt-4 space-y-1.5 rounded-md bg-[hsl(var(--paper-2))] p-3 text-sm">
                {questions.map((q, i) => (
                  <li key={q.id} className="flex gap-2">
                    <span className="font-semibold shrink-0">P{i + 1}</span>
                    <span>
                      {q.questionText}{" "}
                      <span className="text-muted-foreground">
                        · {KIND_LABEL[questionKind(q.questionType)]}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}

            <TabsContent value="person" className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar colaborador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-responses"
                />
              </div>
              {filtered.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nenhum resultado encontrado
                </p>
              ) : (
                <div className="rounded-lg border">
                  <Table wrapperClassName="max-h-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Colaborador</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Time</TableHead>
                        {showAnswerColumns &&
                          questions.map((q, i) => (
                            <TableHead
                              key={q.id}
                              title={q.questionText}
                              className={
                                questionKind(q.questionType) === "scale" ? "text-center" : undefined
                              }
                            >
                              P{i + 1}
                            </TableHead>
                          ))}
                        {canOperateSurveys && <TableHead className="w-10" />}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((response) => {
                        const name = response.employee?.fullName || "Colaborador";
                        return (
                          <TableRow key={response.id} data-testid={`response-row-${response.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                    {initials(name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-medium text-sm whitespace-nowrap">{name}</p>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {formatDateTime(response.submittedAt)}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {responseJobTitle(response)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {responseTeam(response)}
                            </TableCell>
                            {showAnswerColumns &&
                              questions.map((q) => (
                                <TableCell
                                  key={q.id}
                                  className={
                                    questionKind(q.questionType) === "scale"
                                      ? "text-center"
                                      : "min-w-[240px]"
                                  }
                                >
                                  {answerCell(response, q)}
                                </TableCell>
                              ))}
                            {canOperateSurveys && (
                              <TableCell className="p-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      data-testid={`button-menu-response-${response.id}`}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      disabled={resetPending}
                                      onClick={() => onResetResponse({ id: response.id, name })}
                                      data-testid={`button-reset-response-${response.id}`}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5 mr-2" />
                                      Resetar Resposta
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="group" className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={groupBy === "team" ? "default" : "outline"}
                  onClick={() => setGroupBy("team")}
                  data-testid="button-group-by-team"
                >
                  Por time
                </Button>
                <Button
                  size="sm"
                  variant={groupBy === "jobTitle" ? "default" : "outline"}
                  onClick={() => setGroupBy("jobTitle")}
                  data-testid="button-group-by-job-title"
                >
                  Por cargo
                </Button>
              </div>
              {groupBy === "team"
                ? groupedTable(breakdown.byTeam, "Time")
                : groupedTable(breakdown.byJobTitle, "Cargo")}
              <p className="text-xs text-muted-foreground">
                {canViewResponses
                  ? "O time vem do cargo atual de cada pessoa. Nos comentários por time aparece o cargo de quem escreveu, sem o nome."
                  : "Adesão por time e cargo. O time vem do cargo atual de cada pessoa."}
              </p>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
